import { useState } from "react";
import { AuthFormData, AuthMode } from "./useAuthForm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Provider, getProviderConfig, handleProviderError } from "../config/providers";
import { logger } from "@/services/LoggingService";
import { useAuthStore } from "@/stores/authStore";

export function useAuthHandlers() {
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const { 
    setWaitlistDialog, 
    setAuthMethod, 
    setPendingAuthMethod, 
    clearPendingAuthMethod 
  } = useAuthStore();


  const onSubmit = async (values: AuthFormData) => {
    setIsLoading(true);
    try {
      let error;

      switch (authMode) {
        case 'forgot':
          logger.info('auth.email', 'Attempting password reset', { email: values.email });
          const { error: resetError } = await supabase.auth.resetPasswordForEmail(
            values.email,
            {
              redirectTo: `${window.location.origin}/auth?mode=reset`,
            }
          );
          error = resetError;
          if (!error) {
            toast.success("Password reset instructions sent to your email");
            setAuthMode('signin');
          }
          break;

        case 'signup':
          logger.info('auth.email', 'Attempting signup', { email: values.email });
          const { error: signUpError, data: signUpData } = await supabase.auth.signUp({
            email: values.email,
            password: values.password!,
            options: {
              emailRedirectTo: `${window.location.origin}/auth?mode=signup`,
              data: {
                first_name: (values.first_name || '').trim(),
                last_name: (values.last_name || '').trim()
              }
            },
          });
          
          if (signUpError) {
            // Handle specific error cases gracefully
            if (signUpError.message?.includes('User already registered')) {
              toast.error("An account with this email already exists. Please try signing in instead.");
              setAuthMode('signin');
            } else {
              throw signUpError;
            }
          } else if (signUpData.user) {
            logger.info('auth.email', 'Signup successful', {
              userId: signUpData.user.id,
              email: signUpData.user.email
            });
            
            // The user will be handled by the auth state change listener
            // which will call checkUserStatus and handle waitlist logic if needed
            toast.success("Sign up successful! Please check your email to verify your account.");
            setAuthMode('signin');
          }
          break;

        case 'signin':
          logger.info('auth.email', 'Attempting sign in', { email: values.email });
          
          // Clear any stale pending OAuth marker from previous attempts
          clearPendingAuthMethod();
          
          // Clear any existing sessions to prevent conflicts with new login
          logger.debug('auth.email', 'Clearing existing sessions before login');
          await supabase.auth.signOut({ scope: 'local' });
          
          // Small delay to ensure cleanup is complete
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const { error: signInError, data: signInData } = await supabase.auth.signInWithPassword({
            email: values.email,
            password: values.password!,
          });

          if (signInError) {
            logger.error('auth.email', 'Sign in error', { error: signInError });
            throw signInError;
          }

          if (signInData.session) {
            logger.info('auth.email', 'Sign in successful', {
              userId: signInData.user?.id,
              email: signInData.user?.email,
              sessionExpires: signInData.session.expires_at
            });

            // Verify session was stored
            const { data: { session: verifiedSession }, error: verifyError } = await supabase.auth.getSession();
            logger.debug('auth.session', 'Session verification', {
              success: !!verifiedSession,
              error: verifyError?.message,
              sessionExpires: verifiedSession?.expires_at
            });

            toast.success("Successfully signed in!");
            
            // Log email authentication method
            await setAuthMethod('email');
            
            // Navigation is handled by Auth.tsx once user & session are in store
            return;
          } else {
            logger.error('auth.email', 'No session received after sign in');
            throw new Error('No session received after sign in');
          }
          break;
      }

      if (error) throw error;
    } catch (error: any) {
      logger.error('auth.email', 'Authentication error', { error });
      toast.error(error.message || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProviderSignIn = async (provider: Provider) => {
    try {
      // Allow all social login attempts - we'll handle signup restrictions
      // in the AuthContext after the OAuth flow completes

      logger.info('auth.provider', `Initiating ${provider} sign in`, {
        origin: window.location.origin,
        provider
      });

      // Get provider-specific configuration
      const providerConfig = getProviderConfig(provider);
      
      // Special handling for all providers to avoid CSP issues
      // All major providers (Google, Microsoft, LinkedIn) set X-Frame-Options: deny
      // which prevents their login pages from being embedded in iframes
      const useRedirect = provider === 'linkedin_oidc' || provider === 'google' || provider === 'azure';
      
      // Mark the provider so post-redirect we can accurately log the last used method
      setPendingAuthMethod(provider);
      
      // Preserve any existing redirect param so post-OAuth returns user to intended destination
      const currentParams = new URLSearchParams(window.location.search);
      const existingRedirect = currentParams.get('redirect');
      const computedRedirectTo = existingRedirect
        ? `${window.location.origin}/auth?redirect=${encodeURIComponent(existingRedirect)}`
        : (providerConfig.redirectTo || `${window.location.origin}/auth`);

      const { error, data } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          ...providerConfig,
          redirectTo: computedRedirectTo,
          skipBrowserRedirect: !useRedirect
        }
      });
      
      logger.debug('auth.provider', `${provider} sign in result`, {
        success: !error,
        error: error?.message,
        provider: data?.provider,
        url: !!data?.url
      });

      if (error) throw error;
      
      // For redirect-based providers, we rely on browser redirect so no further action needed
      if (useRedirect && data?.url) {
        // Redirect will happen automatically via Supabase
        return;
      }
    } catch (error: any) {
      // Cleanup any pending marker if OAuth failed
      clearPendingAuthMethod();
      const providerError = handleProviderError(error, provider);
      toast.error(providerError.message);
      logger.error('auth.provider', `${provider} sign in error details`, {
        message: error.message,
        stack: error.stack,
        provider
      });
    }
  };

  const handleGoogleSignIn = () => handleProviderSignIn('google');
  const handleMicrosoftSignIn = () => handleProviderSignIn('azure');
  const handleLinkedInSignIn = () => handleProviderSignIn('linkedin_oidc');

  return {
    isLoading,
    authMode,
    setAuthMode,
    onSubmit,
    handleGoogleSignIn,
    handleMicrosoftSignIn,
    handleLinkedInSignIn
  };
}
